import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getComments, getPatientById } from "../../services/Patient";
import useClinic from "../../hooks/useClinic";
import { getClinics } from "../../services/Clinic";
import DownloadButtons from "../../components/UI/DownloadButtons";
import { ValidatePDFContext } from "../../contexts/ValidatePDFContext";
import ProgressionAnalysisReportContainer from "../../containers/ProgressionAnalysisReport";
import Breadcrumb from "../../components/UI/ReportBreadcrumb";
import {
  Box,
  createTheme,
  Grid,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { PageContainerWrapper } from "../../styles/muiStylesHelper";
import { hideLoader, showLoader } from "../../components/loader/loader";
import { useProgressionAnalysisExams } from "../../hooks/progression-analysis/useProgressionAnalysisExams";
import {
  AllFileTypes,
  generateProgressionAnalysisReportFile,
  getProgressionAnalysisAccessInfo,
  getProgressionAnalysisReportInfo,
} from "../../services/Exam";
import { ReportEachSectionContext } from "../../contexts/CommentContext";
import { ReportDataContext } from "../../contexts/ReportDataContext";
import { useReportGeneration } from "../../hooks/reports/useReportGeneration";
import { ErrorMessageComponent } from "../../components/UI/ErrorMessage";
import { sectionCompletionState } from "../../hooks/useExamStatus";

const ProgressionAnalysisReport = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const progressionAnalysisId = location?.pathname?.split(
    "progression-analysis/"
  )?.[1];

  const { patientId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { clinicId, setClinicLicensing } = useClinic();
  const [patient, setPatient] = useState(null);
  const [comments, setComments] = useState([]);

  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Arial", "Verdana", "sans-serif"],
      },
    },
  });

  const isLoading = false;
  const forBackendPdf = false;

  const fetchPatientAndClinic = useCallback(async () => {
    try {
      showLoader();
      const token = await getAccessTokenSilently();
      const [patient, clinicData] = await Promise.all([
        getPatientById(token, clinicId, patientId),
        getClinics(token, clinicId),
      ]);
      hideLoader();

      if (clinicData?.licensing) setClinicLicensing(clinicData.licensing);
      setPatient(patient);
    } catch (e) {
      hideLoader();
      console.error("Error fetching data:", e);
    }
  }, [clinicId, getAccessTokenSilently, patientId, setClinicLicensing]);

  useEffect(() => {
    fetchPatientAndClinic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { analysisData, examsData: report } = useProgressionAnalysisExams({
    useToken: true,
    clinicId,
    patientId,
    progressionAnalysisId,
  });

  const getAllComments = useCallback(async () => {
    try {
      showLoader();
      const token = await getAccessTokenSilently();
      const res = await getComments(
        token,
        clinicId,
        patientId,
        progressionAnalysisId
      );
      setComments(res);
      hideLoader();
    } catch (error) {
      hideLoader();
    }
  }, [clinicId, patientId, progressionAnalysisId, getAccessTokenSilently]);

  useEffect(() => {
    getAllComments();
  }, [getAllComments]);

  const customGetAccessInfo = async (fileType, signal) => {
    const token = await getAccessTokenSilently();
    return getProgressionAnalysisAccessInfo(
      token,
      clinicId,
      patientId,
      progressionAnalysisId,
      fileType,
      signal
    );
  };

  const customGetReportInfo = async (signal) => {
    const token = await getAccessTokenSilently();
    return getProgressionAnalysisReportInfo(
      token,
      clinicId,
      patientId,
      progressionAnalysisId,
      signal
    );
  };

  const customGenerateReportFile = async (type) => {
    const token = await getAccessTokenSilently();
    return generateProgressionAnalysisReportFile(
      token,
      clinicId,
      patientId,
      progressionAnalysisId,
      AllFileTypes[type]
    );
  };

  const {
    downloadPdf,
    shouldDisplayDicomButton,
    shouldDisplayPngButton,
    fileInfo,
    reportInfoError,
    getReportInfoData,
  } = useReportGeneration({
    getAccessInfo: customGetAccessInfo,
    generateReportFile: customGenerateReportFile,
    getReportInfo: customGetReportInfo,
  });

  const refetchComment = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setComments(
        await getComments(token, clinicId, patientId, progressionAnalysisId)
      );
    } catch (e) {
      console.error(e);
    }
  }, [getAccessTokenSilently, clinicId, patientId, progressionAnalysisId]);

  if (analysisData?.state !== sectionCompletionState.Valid) {
    return (
      <Box height="100vh">
        <ErrorMessageComponent />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ValidatePDFContext.Provider value={{ forBackendPdf }}>
        <ReportDataContext.Provider
          value={{ refetchComment, getReportInfoData }}
        >
          <ReportEachSectionContext.Provider value={{ comments }}>
            <Box
              sx={() => PageContainerWrapper()}
              px={{ xs: 2, sm: 4, md: 8, lg: 12 }}
              mb={{ xs: 2 }}
            >
              <Breadcrumb
                isLoading={isLoading}
                patientId={patientId}
                patientName={`${patient?.firstName} ${patient?.lastName}`}
                pageName={t("Progression Report")}
              />
              <Typography fontWeight="bold" my="16px">
                {t("Progression Report")}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap="16px" width="100%">
                <DownloadButtons
                  isLoading={isLoading}
                  fileInfo={fileInfo}
                  reportInfoError={reportInfoError}
                  downloadPdf={downloadPdf}
                  shouldDisplayDicomButton={shouldDisplayDicomButton}
                  shouldDisplayPngButton={shouldDisplayPngButton}
                />
              </Box>
              {report && (
                <Grid container item xs={12} lg={8}>
                  <ProgressionAnalysisReportContainer
                    report={report}
                    patient={patient}
                    clinicId={clinicId}
                    progressionAnalysisId={progressionAnalysisId}
                  />
                </Grid>
              )}
            </Box>
          </ReportEachSectionContext.Provider>
        </ReportDataContext.Provider>
      </ValidatePDFContext.Provider>
    </ThemeProvider>
  );
};

export default ProgressionAnalysisReport;
